// Lightweight document head manager for SEO (title, description, canonical, hreflang, html lang, JSON-LD)
import { useEffect } from "react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export function setPageMeta({ title, description, path, enPath, svPath, lang, jsonLd, robots = "index, follow", image }) {
  if (title) document.title = title;
  if (description) {
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:description", description);
  }
  if (title) upsertMeta("property", "og:title", title);
  if (lang) document.documentElement.lang = lang;
  // Robots directives - public pages index/follow, private pages pass noindex explicitly.
  upsertMeta("name", "robots", robots);
  upsertMeta("name", "googlebot", robots);
  if (image) {
    const imgAbs = image.startsWith("http") ? image : ORIGIN + image;
    upsertMeta("property", "og:image", imgAbs);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:image", imgAbs);
    if (title) upsertMeta("name", "twitter:title", title);
    if (description) upsertMeta("name", "twitter:description", description);
  }
  if (path) {
    upsertLink("canonical", ORIGIN + path);
    upsertMeta("property", "og:url", ORIGIN + path);
  }
  if (enPath) upsertLink("alternate", ORIGIN + enPath, "en");
  if (svPath) upsertLink("alternate", ORIGIN + svPath, "sv");
  // JSON-LD structured data (accepts a single object or an array of objects)
  let script = document.getElementById("page-jsonld");
  if (jsonLd) {
    if (!script) { script = document.createElement("script"); script.id = "page-jsonld"; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
  } else if (script) {
    script.remove();
  }
}

// Set only the robots directive (used by private/dashboard views).
export function setRobots(content) {
  upsertMeta("name", "robots", content);
  upsertMeta("name", "googlebot", content);
}

// React hook wrapper - Level 1 basics.
export const useSEO = ({ title, description, lang, canonical, hreflangEn, hreflangSv, jsonLd, image }) => {
  useEffect(() => {
    setPageMeta({ title, description, path: canonical, enPath: hreflangEn, svPath: hreflangSv, lang, jsonLd, image });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, lang, canonical, hreflangEn, hreflangSv, image, JSON.stringify(jsonLd)]);
};

// BreadcrumbList structured data (Home > Category > City). items: [{ name, path }]
export function buildBreadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.path.startsWith("http") ? it.path : ORIGIN + it.path,
    })),
  };
}

export function buildFaqJsonLd(faq) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://talentsio.com#localbusiness",
    name: "Talentsio",
    alternateName: "Talentsio Sverige",
    description: "Sweden's talent booking marketplace: book DJs, live bands, singers, comedians and hosts for weddings, corporate events and private parties.",
    url: "https://talentsio.com",
    logo: "https://talentsio.com/icon-512.png",
    image: "https://talentsio.com/og/entertainment.jpg",
    priceRange: "3 000 SEK - 60 000 SEK",
    areaServed: [
      { "@type": "City", name: "Stockholm" },
      { "@type": "City", name: "Göteborg" },
      { "@type": "City", name: "Malmö" },
      { "@type": "Country", name: "Sweden" },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Stockholm",
      addressRegion: "Stockholms län",
      postalCode: "111 20",
      addressCountry: "SE",
    },
    geo: { "@type": "GeoCoordinates", latitude: 59.3293, longitude: 18.0686 },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00", closes: "23:59",
    }],
    sameAs: [
      "https://www.instagram.com/talentsio",
      "https://www.tiktok.com/@talentsio",
      "https://linkedin.com/company/talentsio-com",
    ],
  };
}

export function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://talentsio.com#organization",
    name: "Talentsio",
    url: "https://talentsio.com",
    logo: "https://talentsio.com/icon-512.png",
    sameAs: [
      "https://www.instagram.com/talentsio",
      "https://www.tiktok.com/@talentsio",
      "https://linkedin.com/company/talentsio-com",
    ],
    contactPoint: [{
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "hello@talentsio.com",
      areaServed: "SE",
      availableLanguage: ["Swedish", "English"],
    }],
  };
}

export function buildWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://talentsio.com#website",
    url: "https://talentsio.com",
    name: "Talentsio",
    inLanguage: ["sv-SE", "en-GB"],
    publisher: { "@id": "https://talentsio.com#organization" },
    potentialAction: {
      "@type": "SearchAction",
      target: "https://talentsio.com/book?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildServiceJsonLd({ categoryName, cityName, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${categoryName} booking`,
    provider: { "@type": "Organization", name: "Talentsio", url: "https://talentsio.com" },
    areaServed: cityName ? { "@type": "City", name: cityName } : "SE",
    description,
  };
}

// ProfessionalService with a live aggregateRating (only when reviews exist).
export function buildProfessionalServiceJsonLd({ categoryName, cityName, description, ratingAvg, ratingCount }) {
  const node = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: cityName ? `${categoryName} booking in ${cityName}` : `${categoryName} booking`,
    description,
    provider: { "@type": "Organization", name: "Talentsio", url: "https://talentsio.com" },
    areaServed: cityName ? { "@type": "City", name: cityName } : { "@type": "Country", name: "Sweden" },
  };
  if (ratingCount > 0 && ratingAvg > 0) {
    node.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(ratingAvg).toFixed(1),
      reviewCount: ratingCount,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return node;
}

// ItemList of the rendered talent profiles for this category/city.
export function buildItemListJsonLd({ talents, origin, categoryName, cityName }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: cityName ? `${categoryName} in ${cityName}` : categoryName,
    numberOfItems: talents.length,
    itemListElement: talents.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${origin}/talent/${t.id}`,
      name: t.display_name || t.name || "Talent",
    })),
  };
}
